<template>
  <v-app id="app_create_routes" class="bg-white">
    <v-overlay v-model="loading" class="align-center justify-center">
      <v-progress-circular color="#1976D2" indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <v-container fluid>
      <v-row class="pl-2 pr-6">
        <v-col class="mt-3" cols="7">
          <p class="custom_page_main_title">CREATE ROUTE PLAN</p>
          <p class="custom_page_main_sub_title" style="opacity: 0.6">Create route plan according to your requirements.
          </p>
        </v-col>
        <v-col class="px-4 mt-5" cols="5">
          <v-row class="custom_tool_bar_style">
            <v-divider :thickness="3" class="divider-vertical" vertical></v-divider>
            <v-btn class="gray-text" size="x-large" variant="text" @click="goBack()">
              <v-icon size="x-large">mdi-menu-left</v-icon>&nbsp;BACK
            </v-btn>
          </v-row>
        </v-col>
      </v-row>

      <v-card class="custom_card_box mt-8" elevation="1" border="sm opacity-1" rounded="lg">
        <div class="pa-5">
          <p class="text-subtitle-1 font-weight-bold"
            style="color: #4b5162; font-size: 18px !important; letter-spacing: 0.5px;">CREATE ROUTE DETAILS</p>
        </div>
        <v-divider thickness="2"></v-divider>

        <v-card-text class="pa-8">
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field v-model="routeName" :rules="[v => !!v || 'Route name is required']" label="Route Name *"
                  variant="outlined" density="comfortable" placeholder="Type Route Name" maxlength="100"
                  class="custom_input_field_color" clearable></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-autocomplete v-model="assignAgent" :items="filteredAgent"
                                :rules="[v => !!v || 'Sales rep is required']" item-title="salesRepDisplay" item-value="id"
                                label="Assign Sales Rep *" placeholder="Select sales rep" variant="outlined" density="comfortable"
                                class="custom_input_field_color" clearable :disabled="!hasUserId"
                                :loading="loadingAgents" no-data-text="No sales reps found"
                                @update:model-value="onSalesRepChange">
                  <template v-slot:item="{ props, item }">
                    <v-list-item v-bind="props" :title="null" :subtitle="null" class="sales-rep-list-item">
                      <v-list-item-title class="sales-rep-name">{{ item.raw.name }}</v-list-item-title>
                      <v-list-item-subtitle v-if="item.raw.user_code" class="sales-rep-code">{{ item.raw.user_code }}</v-list-item-subtitle>
                    </v-list-item>
                  </template>
                  <template v-slot:no-data>
                    <v-list-item class="sales-rep-list-item">
                      <v-list-item-title>No sales reps found</v-list-item-title>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="4">
                <v-autocomplete v-model="selectedArea" :items="provinces"
                  :rules="[v => (Array.isArray(v) && v.length > 0) || 'At least one area is required']"
                  item-title="area_code" item-value="area_code" label="Area Code *" placeholder="Select area(s)"
                  variant="outlined" density="comfortable" class="custom_input_field_color" clearable multiple
                  chips closable-chips @update:model-value="onAreaChange" :loading="loadingProvinces"
                  :disabled="!assignAgent || !hasUserId" no-data-text="Select a sales rep first" return-object>
                </v-autocomplete>
              </v-col>

              <v-col cols="12" md="4">
                <v-autocomplete v-model="selectedOutletIds" :items="filteredOutlet" :rules="dealerRules"
                  item-title="dealer_name" item-value="id" label="Dealers *" placeholder="Select dealers"
                  variant="outlined" density="comfortable" class="custom_input_field_color" multiple clearable
                  @update:model-value="syncTable" :disabled="!selectedArea || selectedArea.length === 0 || !hasUserId"
                  :loading="loadingOutlets" no-data-text="Select an area first" :error-messages="dealerError">
                  <template v-slot:item="{ props, item }">
                    <v-list-item v-bind="props" :title="null" :subtitle="null">
                      <v-list-item-title class="sales-rep-name">{{ item.raw.dealer_name }}</v-list-item-title>
                      <v-list-item-subtitle v-if="item.raw.dealer_code" class="sales-rep-code">{{ item.raw.dealer_code }}</v-list-item-subtitle>
                    </v-list-item>
                  </template>
                  <template v-slot:no-data>
                    <v-list-item>
                      <v-list-item-title>No dealers found for this area</v-list-item-title>
                    </v-list-item>
                  </template>
                  <template v-slot:selection="{ item, index }">
                    <div v-if="index === 0" class="d-flex align-center" style="gap: 8px; position: relative;">
                      <v-chip color="#e7e7e7" variant="flat" class="custom-selection-chip text-black font-weight-medium"
                        size="small" closable @click:close="removeItem(item.value)">
                        {{ item.raw.dealer_name }}
                      </v-chip>

                      <div v-if="selectedOutletIds.length > 1" class="hover-card-container"
                        @mouseenter="showHoverCard = true" @mouseleave="showHoverCard = false">
                        <v-chip color="#800080" variant="flat" class="custom-count-chip text-white font-weight-bold"
                          size="small">
                          +{{ selectedOutletIds.length - 1 }}
                        </v-chip>

                        <div v-if="showHoverCard" class="hover-card">
                          <div class="hover-card-list">
                            <div v-for="id in selectedOutletIds.slice(1)" :key="id" class="hover-card-item">
                              {{ getDealerNameById(id) }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="12" md="4">
                <v-date-input v-model="assignDate" :rules="[v => !!v || 'Assign date is required']"
                  label="Assign Date *" placeholder="Select Date" append-inner-icon="mdi-close"
                  prepend-inner-icon="$calendar" prepend-icon="" variant="outlined" density="comfortable"
                  class="custom_input_field_color" @click:append-inner="clearAssignDate" :min="minAssignDate"></v-date-input>
              </v-col>
            </v-row>

            <div class="mt-10">
              <v-divider class="mb-6"></v-divider>
              <p class="text-subtitle-1 font-weight-bold"
                style="color: #4b5162; font-size: 16px !important; letter-spacing: 0.5px;">SELECTED ROUTE WITH DEALER
                LIST</p>
              <p class="text-subtitle-2 text-grey-darken-1 mt-1 mb-4">The following table shows all the dealers related
                to the
                above-defined route.</p>

              <v-card variant="outlined" class="custom_card_box elevation-0" rounded="lg"
                style="border: 1px solid #e0e0e0 !important;">
                <v-card-title class="pa-4 bg-white">
                  <v-text-field v-model="search" label="Search" placeholder="Search dealer" variant="outlined"
                    density="comfortable" hide-details prepend-inner-icon="mdi-magnify"
                    class="custom_input_field_color max-width-search"></v-text-field>
                </v-card-title>

                <v-data-table :headers="headers" :items="filteredTableItems" class="custom_table_normal" hover>
                  <template v-slot:[`item.select_action`]="{ item }">
                    <div class="d-flex align-center justify-center" style="gap: 8px;">
                      <span class="action-label" :class="!item.active ? 'active-label' : 'inactive-label'">REMOVE</span>
                      <v-switch v-model="item.active" color="#800080" hide-details density="compact"
                        class="toggle-switch" @change="toggleDealer(item)"></v-switch>
                      <span class="action-label" :class="item.active ? 'active-label' : 'inactive-label'">ADD</span>
                    </div>
                  </template>

                  <template v-slot:[`item.dealer_name`]="{ item }">
                    <div class="py-2">
                      <div class="dealer-name">{{ item.dealer_name }}</div>
                      <div class="reg-num">Dealer Code: {{ item.dealer_code }}</div>
                    </div>
                  </template>

                  <template v-slot:[`item.outstanding`]="{ item }">
                    <div class="outstanding-amount">
                      <span class="amount">LKR {{ formatOutstanding(item.outstanding) }}</span>
                    </div>
                  </template>

                  <template v-slot:[`item.area_display`]="{ item }">
                    <span class="table-text text-uppercase">{{ item.area_code }}</span>
                  </template>

                  <template v-slot:[`item.address`]="{ item }">
                    <div class="address-text">
                      {{ item.address || 'N/A' }}
                    </div>
                  </template>

                  <template v-slot:[`item.action`]="{ item }">
                    <v-btn class="view-invoice-button" size="small" variant="flat" @click="viewInvoice(item)">
                      <v-icon size="small" class="mr-1">mdi-receipt</v-icon>
                      VIEW INVOICE
                    </v-btn>
                  </template>
                </v-data-table>
              </v-card>
            </div>
          </v-form>
        </v-card-text>

        <v-divider thickness="2"></v-divider>

        <v-card-actions class="pa-6 justify-end">
          <v-btn v-if="permissions.write" class="custom_btn_primary px-8" size="large" variant="flat" @click="submitForm" :loading="loading"
            :disabled="!hasUserId">
            CREATE ROUTE
          </v-btn>
          <v-btn class="custom_btn_secondary px-8 ml-4" size="large" variant="flat" @click="cancelForm">
            CANCEL
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-container>

    <v-dialog v-model="invoiceDialog" max-width="1400" transition="dialog-bottom-transition" scrollable>
      <v-card class="invoice-dialog-card">
        <v-card-title class="invoice-dialog-title pa-5">
          <div class="d-flex align-center justify-space-between">
            <div>
              <h3 class="text-h5 font-weight-bold">DEALER SUMMARY</h3>
              <p class="text-subtitle-1 mt-1 mb-0" style="color: #666;">
                {{ selectedDealer?.dealer_name }} - DEALER CODE: {{ selectedDealer?.dealer_code }}
              </p>
            </div>
            <v-btn icon="mdi-close" variant="text" @click="closeInvoiceDialog"></v-btn>
          </div>
        </v-card-title>
        <v-divider></v-divider>
        
        <v-card-text class="pa-5 invoice-dialog-content">
          <v-row class="mb-4">
            <v-col cols="12" md="6">
              <v-text-field
                v-model="invoiceSearch"
                label="Search Outstanding Data"
                placeholder="Search by customer, document, or status..."
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-magnify"
                clearable
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6" class="text-right">
              <div class="summary-cards">
                <v-chip class="summary-chip" color="success" variant="flat">
                  Total Records: {{ filteredInvoiceItems.length }}
                </v-chip>
                <v-chip class="summary-chip" color="primary" variant="flat" v-if="totalOutstanding > 0">
                  Total Outstanding: LKR {{ formatOutstanding(totalOutstanding) }}
                </v-chip>
              </div>
            </v-col>
          </v-row>

          <v-data-table
            :headers="invoiceHeaders"
            :items="filteredInvoiceItems"
            :search="invoiceSearch"
            class="invoice-table custom_table_normal"
            hover
            :loading="loadingInvoices"
            loading-text="Loading outstanding records..."
            no-data-text="No outstanding records found for this customer"
          >
            <template v-slot:[`item.customer_no`]="{ item }">
              <span class="invoice-document-no">{{ item.customer_no }}</span>
            </template>

            <template v-slot:[`item.name`]="{ item }">
              <span class="font-weight-bold" style="color: #2C3E50;">{{ item.name }}</span>
            </template>

            <template v-slot:[`item.document_type`]="{ item }">
              <v-chip :color="getDocumentTypeColor(item.document_type)" size="small" variant="flat" class="document-type-chip">
                {{ item.document_type || 'N/A' }}
              </v-chip>
            </template>

            <template v-slot:[`item.document_no`]="{ item }">
              <span class="invoice-document-no">{{ item.document_no || 'N/A' }}</span>
            </template>

            <template v-slot:[`item.posting_date`]="{ item }">
              <span class="invoice-date">{{ formatDateDisplay(item.posting_date) }}</span>
            </template>

            <template v-slot:[`item.due_date`]="{ item }">
              <span class="invoice-date" :class="{'text-error': isOverdue(item.due_date)}">{{ formatDateDisplay(item.due_date) }}</span>
            </template>

            <template v-slot:[`item.amount`]="{ item }">
              <span :class="getAmountClass(item.amount)" class="font-weight-bold">
                LKR {{ formatOutstanding(item.amount) }}
              </span>
            </template>

            <template v-slot:[`item.remaining_amount`]="{ item }">
              <span :class="getRemainingAmountClass(item.remaining_amount)" class="font-weight-bold">
                LKR {{ formatOutstanding(item.remaining_amount) }}
              </span>
            </template>

            <template v-slot:[`item.assigned_amount`]="{ item }">
              <span :class="getAssignedAmountClass(item.assigned_amount)" class="font-weight-bold">
                LKR {{ formatOutstanding(item.assigned_amount) }}
              </span>
            </template>

            <template v-slot:[`item.actual_remaining_amount`]="{ item }">
              <span :class="getRemainingAmountClass(item.actual_remaining_amount)" class="font-weight-bold">
                LKR {{ formatOutstanding(item.actual_remaining_amount) }}
              </span>
            </template>

            <template v-slot:[`item.status`]="{ item }">
              <v-chip :color="getStatusColor(item.status)" size="small" variant="flat" class="document-type-chip">
                {{ item.status || 'N/A' }}
              </v-chip>
            </template>
          </v-data-table>
        </v-card-text>

        <v-divider></v-divider>
        
        <v-card-actions class="pa-4 justify-end">
          <v-btn variant="text" @click="closeInvoiceDialog" class="mr-2">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" location="top right" timeout="3000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-app>
</template>

<script>
import axios from 'axios';
import { getPermissions } from "@/plugins/permissions";

export default {
  data() {
    return {
      permissions: { read: false, write: false, delete: false },
      valid: false,
      loading: false,
      loadingProvinces: false,
      loadingAgents: false,
      loadingOutlets: false,
      loadingInvoices: false,
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: '',
      search: '',
      routeName: '',
      selectedArea: [],
      assignAgent: null,
      assignDate: null,
      selectedOutletIds: [],
      tableItems: [],
      showHoverCard: false,
      dealerError: '',
      userLatitude: null,
      userLongitude: null,

      invoiceDialog: false,
      selectedDealer: null,
      invoiceItems: [],
      invoiceSearch: '',

      currentUserIdValue: null,
      currentUserCompanyValue: 'Piyestra Furniture Pvt. Ltd',

      provinces: [],
      agentDataOptions: [],
      dealerOptions: [],
      filteredAgent: [],
      filteredOutlet: [],

      headers: [
        { title: 'SELECT / REMOVE', key: 'select_action', align: 'center', sortable: false, width: '15%' },
        { title: 'DEALER NAME', key: 'dealer_name', align: 'start', width: '25%' },
        { title: 'OUTSTANDING', key: 'outstanding', align: 'start', width: '15%' },
        { title: 'AREA', key: 'area_display', align: 'start', width: '10%' },
        { title: 'ADDRESS', key: 'address', align: 'start', width: '25%' },
        { title: 'ACTION', key: 'action', align: 'start', sortable: false, width: '10%' },
      ],

      invoiceHeaders: [
        { title: 'DOCUMENT TYPE', key: 'document_type', align: 'center', width: '9%' },
        { title: 'DOCUMENT NO', key: 'document_no', align: 'start', width: '10%' },
        { title: 'POSTING DATE', key: 'posting_date', align: 'center', width: '10%' },
        { title: 'DUE DATE', key: 'due_date', align: 'center', width: '10%' },
        { title: 'AMOUNT', key: 'amount', align: 'end', width: '9%' },
        { title: 'REMAINING AMOUNT', key: 'remaining_amount', align: 'end', width: '11%' },
        { title: 'ASSIGNED AMOUNT', key: 'assigned_amount', align: 'end', width: '9%' },
        { title: 'ACTUAL REMAINING AMOUNT', key: 'actual_remaining_amount', align: 'end', width: '11%' },
        { title: 'STATUS', key: 'status', align: 'center', width: '8%' },
      ],
    };
  },

  watch: {
    assignDate(newVal) {
      if (newVal) {
        const selectedDate = new Date(newVal);
        const minDate = new Date(this.minAssignDate + 'T00:00:00');
        
        selectedDate.setHours(0, 0, 0, 0);
        minDate.setHours(0, 0, 0, 0);
        
        if (selectedDate < minDate) {
          this.assignDate = minDate;
        }
      }
    }
  },

  computed: {
    hasUserId() {
      return !!this.currentUserIdValue;
    },

    minAssignDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    dealerRules() {
      return [
        (v) => {
          if (this.dealerError) {
            return v.length > 0 || this.dealerError;
          }
          return true;
        }
      ];
    },

    filteredInvoiceItems() {
      let filtered = [...this.invoiceItems];
      
      if (this.invoiceSearch) {
        const searchLower = this.invoiceSearch.toLowerCase().trim();
        filtered = filtered.filter(item => 
          (item.customer_no?.toLowerCase() || '').includes(searchLower) ||
          (item.name?.toLowerCase() || '').includes(searchLower) ||
          (item.document_type?.toLowerCase() || '').includes(searchLower) ||
          (item.document_no?.toLowerCase() || '').includes(searchLower) ||
          (item.posting_date?.toLowerCase() || '').includes(searchLower) ||
          (item.due_date?.toLowerCase() || '').includes(searchLower) ||
          String(item.amount ?? '').toLowerCase().includes(searchLower) ||
          String(item.remaining_amount ?? '').toLowerCase().includes(searchLower) ||
          String(item.assigned_amount ?? '').toLowerCase().includes(searchLower) ||
          String(item.actual_remaining_amount ?? '').toLowerCase().includes(searchLower) ||
          (item.status?.toLowerCase() || '').includes(searchLower)
        );
      }
      
      return filtered;
    },

    totalOutstanding() {
      return this.filteredInvoiceItems.reduce((sum, item) => {
        const amount = parseFloat(item.amount || 0);
        return sum + (amount > 0 ? amount : 0);
      }, 0);
    },

    filteredTableItems() {
      const query = (this.search || '').toString().toLowerCase().trim();
      if (!query) return this.tableItems;

      return this.tableItems.filter((item) => {
        const searchableValues = [
          item.dealer_name,
          item.dealer_code,
          item.address,
          item.area_code,
          item.mobile_number,
          item.company_name
        ]
          .filter((value) => value !== null && value !== undefined)
          .map((value) => String(value).toLowerCase());

        return searchableValues.some((value) => value.includes(query));
      });
    }
  },

  methods: {
    isOverdue(dueDate) {
      if (!dueDate) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const due = new Date(dueDate);
      due.setHours(0, 0, 0, 0);
      return due < today;
    },

    buildSubmitErrorMessage(error) {
      const responseData = error?.response?.data;

      if (responseData && typeof responseData === 'object') {
        const baseMessage = responseData.message || responseData.error || 'Error saving route';
        const details = [];

        if (responseData.route_name) {
          details.push(`Route: ${responseData.route_name}`);
        }

        if (responseData.dealer_code) {
          details.push(`Dealer Code: ${responseData.dealer_code}`);
        }

        return details.length > 0 ? `${baseMessage} (${details.join(' | ')})` : baseMessage;
      }

      return error?.message || 'Error saving route';
    },

    getUserIdFromStorage() {
      const userId = localStorage.getItem('userId');
      if (userId) {
        return parseInt(userId);
      }
      return null;
    },

    async initializeUserData(maxRetries = 5) {
      let retries = 0;
      while (retries < maxRetries) {
        const userId = this.getUserIdFromStorage();
        if (userId) {
          this.currentUserIdValue = userId;
          this.currentUserCompanyValue = 'Piyestra Furniture Pvt. Ltd';
          return true;
        }
        await new Promise(resolve => setTimeout(resolve, 200));
        retries++;
      }
      this.snackbarMessage = 'User session not found. Please login again.';
      this.snackbarColor = 'error';
      this.snackbar = true;
      return false;
    },

    // Get user's current location
    async getUserLocation() {
      return new Promise((resolve) => {
        if (!navigator.geolocation) {
          console.log('Geolocation is not supported');
          // Default coordinates for Sri Lanka
          resolve({ lat: 7.8731, lng: 80.7718 });
          return;
        }
        
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            });
          },
          (error) => {
            console.log('Error getting location:', error);
            // Default coordinates for Sri Lanka
            resolve({ lat: 7.8731, lng: 80.7718 });
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          }
        );
      });
    },

    clearAssignDate() {
      this.assignDate = null;
    },

    removeItem(id) {
      this.selectedOutletIds = this.selectedOutletIds.filter(selectedId => selectedId !== id);
      this.syncTable(this.selectedOutletIds);
      if (this.selectedOutletIds.length > 0) {
        this.dealerError = '';
      }
    },

    getDealerNameById(id) {
      const dealer = this.dealerOptions.find(d => d.id === id);
      return dealer ? dealer.dealer_name : '';
    },

    formatOutstanding(amount) {
      if (!amount && amount !== 0) return '0.00';
      return parseFloat(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    formatDateDisplay(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    },

    getAmountClass(amount) {
      const value = parseFloat(amount);
      if (value < 0) return 'amount-negative';
      if (value > 0) return 'amount-positive';
      return 'amount-neutral';
    },

    getRemainingAmountClass(amount) {
      const value = parseFloat(amount);
      if (value > 0) return 'remaining-positive';
      if (value === 0) return 'remaining-zero';
      return 'remaining-negative';
    },

    getAssignedAmountClass(amount) {
      const value = parseFloat(amount);
      if (value < 0) return 'assigned-negative';
      if (value === 0) return 'assigned-zero';
      return 'assigned-positive';
    },

    getDocumentTypeColor(documentType) {
      if (documentType === 'Invoice') return 'success';
      if (documentType === 'Credit Memo') return 'warning';
      return 'default';
    },

    getStatusColor(status) {
      if (status === 'UnPaid') return 'error';
      if (status === 'Overdue') return 'warning';
      if (status === 'Paid') return 'success';
      return 'default';
    },

    /**
     * ✅ FIXED: Load the Sales Rep dropdown scoped to the CURRENTLY LOGGED-IN
     * user's own assigned area codes (not tied to Area selection anymore).
     * Uses /api/getSalesRepByUserAreaCodes.
     */
    async fetchSalesRepsForCurrentUser() {
      if (!this.currentUserIdValue) {
        console.warn('No user ID available for fetching sales reps');
        this.filteredAgent = [];
        return;
      }
      
      this.loadingAgents = true;
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          console.warn('No token found');
          this.filteredAgent = [];
          return;
        }

        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/getSalesRepByUserAreaCodes`,
          {
            params: { userId: this.currentUserIdValue },
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
          }
        );

        if (response.data && response.data.success && Array.isArray(response.data.data)) {
          this.agentDataOptions = response.data.data.map(agent => ({
            id: agent.id,
            name: agent.name || agent.user_name || 'Unknown',
            user_name: agent.user_name || '',
            user_code: agent.user_code || '',
            registration_num: agent.registration_num || '',
            salesRepDisplay: agent.name || agent.user_name || 'Unknown'
          }));
          this.filteredAgent = this.agentDataOptions;
        } else {
          console.warn('No sales reps data or invalid response structure:', response.data);
          this.agentDataOptions = [];
          this.filteredAgent = [];
        }
      } catch (error) {
        console.error('Error fetching sales reps:', error);
        this.agentDataOptions = [];
        this.filteredAgent = [];
        
        // Don't show error snackbar for this - it's not critical for the page to render
        // Just log it and continue
      } finally {
        this.loadingAgents = false;
      }
    },

    /**
     * ✅ CHANGED: fetchAreas now accepts an optional userId so it can load
     * the area codes for whichever Sales Rep was selected (agentId), not
     * only the logged-in user. Falls back to the logged-in user if no
     * userId is passed (kept for safety, though the new flow always
     * passes the selected agent's id).
     */
    async fetchAreas(forUserId = null) {
      this.loadingProvinces = true;
      try {
        const targetUserId = forUserId || this.currentUserIdValue || this.getUserIdFromStorage();
        if (!targetUserId) {
          this.provinces = [];
          return;
        }

        const token = localStorage.getItem('token');
        if (!token) {
          this.provinces = [];
          return;
        }

        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/area/user/${encodeURIComponent(targetUserId)}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data && response.data.success && Array.isArray(response.data.data)) {
          this.provinces = response.data.data
            .map((area, index) => {
              if (typeof area === 'string') {
                return {
                  id: index + 1,
                  area_code: area,
                  description: '',
                  region_code: ''
                };
              }

              if (area && typeof area === 'object') {
                return {
                  id: area.id ?? index + 1,
                  area_code: area.area_code || area.code || '',
                  description: area.description || '',
                  region_code: area.region_code || ''
                };
              }

              return null;
            })
            .filter(area => area && area.area_code);
        } else {
          this.provinces = [];
        }
      } catch (error) {
        console.error('Error fetching areas:', error);
        this.snackbarMessage = 'Failed to load areas for the selected sales rep.';
        this.snackbarColor = 'error';
        this.snackbar = true;
        this.provinces = [];
      } finally {
        this.loadingProvinces = false;
      }
    },

    /**
     * ✅ Fetch dealers for ONE area code and return the mapped array
     * (does not mutate state itself) — used by onAreaChange() to merge
     * results from multiple selected area codes without wiping out
     * dealers/selections already loaded for other areas.
     */
    async fetchDealersForArea(areaCode, salespersonCode = '') {
      if (!this.currentUserIdValue) return [];
      try {
        const token = localStorage.getItem('token');
        if (!token) return [];

        const requestBody = {
          userId: this.currentUserIdValue,
          area_code: areaCode,
          salesperson_code: salespersonCode || ''
        };

        const requestParams = Object.fromEntries(
          Object.entries(requestBody).filter(([, value]) => value !== '' && value !== null && value !== undefined)
        );

        const response = await axios({
          method: 'get',
          url: `${process.env.VUE_APP_API_BASE_URL}/api/dealer/getDealerListRoute`,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          params: requestParams,
          data: requestBody
        });

        let allDealers = [];
        if (response.data?.success && Array.isArray(response.data.data)) {
          allDealers = response.data.data;
        }

        return allDealers.map((dealer, index) => {
          const dealerCode = dealer.dealer_code || dealer.code || dealer.dealerCode || `${areaCode}-${index}`;
          return {
            id: dealerCode,
            dealer_name: dealer.dealer_name || dealer.name || dealer.dealerName || 'Unknown Dealer',
            dealer_code: dealerCode,
            company_name: dealer.company_name || dealer.company || '',
            address: dealer.address || dealer.dealer_address || '',
            region_code: dealer.region_code || dealer.region || '',
            area_code: dealer.area_code || dealer.areaCode || dealer.area || areaCode,
            mobile_number: dealer.mobile_number || dealer.phone || '',
            mobile_number2: dealer.mobile_number2 || dealer.phone2 || '',
            outstanding: dealer.total_outstanding || dealer.outstanding || dealer.outstanding_amount || 0,
            total_document_count: dealer.total_document_count || 0,
            companies: dealer.companies || [],
            latitude: dealer.lat || dealer.latitude || null,
            longitude: dealer.lng || dealer.longitude || null
          };
        });
      } catch (error) {
        console.error('Error fetching dealers for area:', error);
        this.snackbarMessage = 'Failed to load dealers for one of the selected areas. Please try again.';
        this.snackbarColor = 'error';
        this.snackbar = true;
        return [];
      }
    },

    /**
     * ✅ CHANGED: Sales Rep is now selected FIRST. Choosing one loads the
     * AREA dropdown scoped to that sales rep's own assigned areas
     * (instead of areas driving the sales rep list).
     */
    onSalesRepChange(agentId) {
      // Reset everything downstream since the sales rep changed
      this.selectedArea = [];
      this.selectedOutletIds = [];
      this.tableItems = [];
      this.dealerError = '';
      this.provinces = [];
      this.dealerOptions = [];
      this.filteredOutlet = [];

      if (!agentId || !this.currentUserIdValue) return;

      this.fetchAreas(agentId);
    },

    async fetchDealerSummary(dealer) {
      this.loadingInvoices = true;
      try {
        if (!this.currentUserIdValue || !dealer?.dealer_code) {
          this.invoiceItems = [];
          return;
        }

        const token = localStorage.getItem('token');
        if (!token) {
          this.invoiceItems = [];
          return;
        }

        const requestParams = {
          userId: this.currentUserIdValue,
          customer_no: dealer.dealer_code
        };

        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/master-outstanding/CustomerAndSales`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            params: requestParams
          }
        );

        if (response.data && response.data.success) {
          if (Array.isArray(response.data.data) && response.data.data.length > 0) {
            // Normalize response keys for table/search rendering
            this.invoiceItems = response.data.data.map((row) => ({
              id: row.id || Math.random().toString(36).substr(2, 9),
              customer_no: row.customer_no || dealer.dealer_code,
              name: row.name || dealer.dealer_name || 'N/A',
              posting_date: row.posting_date || 'N/A',
              due_date: row.due_date || 'N/A',
              document_type: row.document_type || 'N/A',
              document_no: row.document_no || 'N/A',
              amount: row.amount || 0,
              remaining_amount: row.remaining_amount || 0,
              assigned_amount: row.assigned_amount || 0,
              actual_remaining_amount: row.actual_remaining_amount || 0,
              status: row.status || 'N/A'
            }));
          } else {
            this.invoiceItems = [];
          }
        } else {
          this.invoiceItems = [];
        }
      } catch (error) {
        console.error('Error fetching dealer summary:', error);
        this.snackbarMessage = error.response?.data?.message || 'Failed to load outstanding records. Please try again.';
        this.snackbarColor = 'error';
        this.snackbar = true;
        this.invoiceItems = [];
      } finally {
        this.loadingInvoices = false;
      }
    },

    /**
     * ✅ CHANGED: Area is now a MULTI-select. Adding a new area code
     * MERGES its dealers into the existing list without touching dealers
     * or selections already loaded for other selected areas. Only
     * removing an area code clears the dealers/selections that belonged
     * to that specific area.
     */
    async onAreaChange(newAreaSelection) {
      this.dealerError = '';

      const newAreaCodes = (newAreaSelection || [])
        .map(a => (typeof a === 'string' ? a : a?.area_code))
        .filter(Boolean);

      // ── Remove dealers/selections belonging to areas that were just
      // ── DE-selected. Dealers from areas still selected are left as-is.
      const currentlyLoadedAreaCodes = [...new Set(this.dealerOptions.map(d => d.area_code))];
      const removedAreaCodes = currentlyLoadedAreaCodes.filter(code => !newAreaCodes.includes(code));

      if (removedAreaCodes.length > 0) {
        this.dealerOptions = this.dealerOptions.filter(d => !removedAreaCodes.includes(d.area_code));
        this.filteredOutlet = this.dealerOptions;
        this.selectedOutletIds = this.selectedOutletIds.filter(id =>
          this.dealerOptions.some(d => d.id === id)
        );
        this.syncTable(this.selectedOutletIds);
      }

      // ── Fetch dealers ONLY for newly added area codes, then merge in ──
      const alreadyLoadedAreaCodes = [...new Set(this.dealerOptions.map(d => d.area_code))];
      const codesToFetch = newAreaCodes.filter(code => !alreadyLoadedAreaCodes.includes(code));

      if (codesToFetch.length === 0 || !this.currentUserIdValue) return;

      const selectedAgent = this.agentDataOptions.find(agent => agent.id === this.assignAgent);
      const salespersonCode = selectedAgent?.user_code || selectedAgent?.registration_num || '';

      this.loadingOutlets = true;
      try {
        const results = await Promise.all(
          codesToFetch.map(code => this.fetchDealersForArea(code, salespersonCode))
        );
        const newDealers = results.flat();

        const existingIds = new Set(this.dealerOptions.map(d => d.id));
        const merged = [...this.dealerOptions, ...newDealers.filter(d => !existingIds.has(d.id))];

        this.dealerOptions = merged;
        this.filteredOutlet = merged;
      } catch (error) {
        console.error('Error loading dealers:', error);
      } finally {
        this.loadingOutlets = false;
      }
    },

    syncTable(ids) {
      this.tableItems = this.filteredOutlet
        .filter(d => ids.includes(d.id))
        .map(d => ({
          ...d,
          active: true,
          area_display: d.area_code
        }));

      if (ids.length > 0) {
        this.dealerError = '';
      }
    },

    toggleDealer(item) {
      if (!item.active) {
        this.selectedOutletIds = this.selectedOutletIds.filter(id => id !== item.id);
      } else {
        if (!this.selectedOutletIds.includes(item.id)) {
          this.selectedOutletIds.push(item.id);
        }
      }
      this.syncTable(this.selectedOutletIds);
    },

    async viewInvoice(dealer) {
      this.selectedDealer = dealer;
      this.invoiceDialog = true;
      this.invoiceSearch = '';
      await this.fetchDealerSummary(dealer);
    },

    closeInvoiceDialog() {
      this.invoiceDialog = false;
      this.selectedDealer = null;
      this.invoiceItems = [];
      this.invoiceSearch = '';
    },

    exportInvoiceData() {
      const headers = this.invoiceHeaders.map(h => h.title).join(',');
      const rows = this.filteredInvoiceItems.map(item => {
        return [
          item.customer_no,
          `"${item.name}"`,
          `"${item.document_type}"`,
          item.document_no,
          item.posting_date,
          item.due_date,
          item.amount,
          item.remaining_amount,
          item.assigned_amount,
          item.actual_remaining_amount,
          item.status
        ].join(',');
      });
      
      const csv = [headers, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customer_outstanding_${this.selectedDealer?.dealer_code}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      this.snackbarMessage = 'Data exported successfully';
      this.snackbarColor = 'success';
      this.snackbar = true;
    },

    async submitForm() {
      this.dealerError = '';

      if (!this.selectedOutletIds || this.selectedOutletIds.length === 0) {
        this.dealerError = 'At least one dealer is required';
        this.snackbarMessage = 'Please select at least one dealer';
        this.snackbarColor = 'error';
        this.snackbar = true;
        return;
      }

      const { valid } = await this.$refs.form.validate();
      if (!valid) return;

      if (!this.currentUserIdValue) {
        this.snackbarMessage = 'User not authenticated. Please login again.';
        this.snackbarColor = 'error';
        this.snackbar = true;
        return;
      }

      this.loading = true;

      try {
        console.log('Submitting payload:');
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Authentication token not found');
        }
        
        if (!this.selectedArea || this.selectedArea.length === 0) {
          throw new Error('Please select at least one area');
        }

        const areaCodes = this.selectedArea
          .map(a => (typeof a === 'string' ? a : a?.area_code))
          .filter(Boolean);

        const dealers = this.tableItems
          .filter(item => item.active)
          .map(item => ({
            dealer_name: item.dealer_name,
            dealer_code: item.dealer_code,
            address: item.address,
            total_outstanding: item.outstanding || 0,
            status: "PENDING",
            latitude: item.latitude || null,
            longitude: item.longitude || null
          }));

        const selectedAgent = this.agentDataOptions.find(agent => agent.id === this.assignAgent);
        const saleRepCode = selectedAgent?.user_code || selectedAgent?.registration_num || `${this.assignAgent || ''}`;

        const payload = {
          userId: this.currentUserIdValue,
          route_name: this.routeName,
          route_category: "Assign Route Plan",
          area_code: areaCodes.join(','),
          assign_date: this.formatDate(this.assignDate),
          assigner_id: this.currentUserIdValue,
          saleRep_id: this.assignAgent,
          saleRep_code: saleRepCode,
          reason: "New route",
          dealers: dealers
        };

        console.log('Submitting payload:', payload);

        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/api/route/v1/create`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data && response.data.success) {
          this.snackbarMessage = 'Route Plan Created Successfully';
          this.snackbarColor = 'success';
          this.snackbar = true;
          this.cancelForm();
          setTimeout(() => {
            this.$router.push('/routes-list');
          }, 1500);
        } else {
          this.snackbarMessage = this.buildSubmitErrorMessage({ response: { data: response.data } });
          this.snackbarColor = 'error';
          this.snackbar = true;
        }
      } catch (err) {
        console.error('Submit error:', err);
        this.snackbarMessage = this.buildSubmitErrorMessage(err);
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.loading = false;
      }
    },

    formatDate(date) {
      if (!date) return null;
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    goBack() {
      this.$router.go(-1);
    },

    cancelForm() {
      this.$refs.form.reset();
      this.tableItems = [];
      this.routeName = '';
      this.selectedArea = [];
      this.assignAgent = null;
      this.assignDate = null;
      this.selectedOutletIds = [];
      this.dealerOptions = [];
      this.filteredOutlet = [];
      this.provinces = [];
      this.dealerError = '';

      // ✅ Re-load the sales rep list (scoped to the logged-in user's areas)
      // since Sales Rep is the first cascade step again after reset.
      this.fetchSalesRepsForCurrentUser();
    }
  },

  async mounted() {
    this.permissions = getPermissions(0, "Module Name");

    // CHECK READ ACCESS
    if (!this.permissions.read) {
      this.showSnackbar ? this.showSnackbar("Access Denied", "error") : alert("Access Denied");
      this.$router.push('/dashboard');
      return;
    }
    console.log('=== CreateRoutes Mounted ===');

    // Initialize user data first with retry
    const initialized = await this.initializeUserData();
    if (initialized) {
      // Get user location first
      const location = await this.getUserLocation();
      this.userLatitude = location.lat;
      this.userLongitude = location.lng;
      console.log('User location set:', { lat: this.userLatitude, lng: this.userLongitude });

      // ✅ CHANGED: Load Sales Reps first, scoped to the logged-in user's
      // own area codes. Area dropdown now waits for a Sales Rep to be
      // selected (see onSalesRepChange) instead of loading immediately.
      await this.fetchSalesRepsForCurrentUser();
    }
  }
};
</script>

<style lang="scss" scoped>
@use '../RouteManagement/RouteManagement.scss' as *;
</style>

<style lang="scss">
#app_create_routes {
  .custom_page_main_title {
    font-size: 24px !important;
    font-weight: 900 !important;
    color: #4b5162 !important;
    margin-bottom: 4px;
    letter-spacing: 0.5px;
  }

  .custom_page_main_sub_title {
    font-size: 16px !important;
    color: #757575 !important;
  }

  .custom_tool_bar_style {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .divider-vertical {
    height: 40px;
    border-color: #bdbdbd !important;
    margin-right: 16px;
  }

  .gray-text {
    color: #757575 !important;
    font-weight: bold;
    letter-spacing: 1px;
  }

  .custom_card_box {
    font-family: var(--font-family, 'Inter', 'Roboto', sans-serif) !important;
    border-top-left-radius: var(--border_top_left_radius, 8px) !important;
    border-top-right-radius: var(--border_top_right_radius, 8px) !important;
    border-bottom-right-radius: var(--border_bottom_right_radius, 8px) !important;
    box-shadow: 0px 4px 18px 0px #0000001F !important;
    background-color: #ffffff;
  }

  .custom_input_field_color {
    .v-field__input {
      color: #333333 !important;
    }

    :deep(.v-label) {
      font-size: 14px;
      font-weight: 500;
      color: #444;
    }

    :deep(.v-field__outline) {
      --v-field-border-opacity: 0.25;
    }
  }

  .custom_table_normal {
    border-top: 1px solid #e0e0e0;
    margin-top: 10px;

    :deep(thead th) {
      font-weight: bold !important;
      color: #616161 !important;
      background-color: #fafafa !important;
      text-transform: uppercase;
      font-size: 13px !important;
      border-bottom: 2px solid #EEEEEE !important;
    }

    :deep(.v-data-table__td) {
      border-bottom: 1px solid #F0F0F0 !important;
    }
  }

  .custom_btn_primary {
    background-color: #800080 !important;
    color: #ffffff !important;
    font-weight: bold;
    letter-spacing: 1px;
    border-radius: 4px;
  }

  .custom_btn_secondary {
    background-color: #e0e0e0 !important;
    color: #424242 !important;
    font-weight: bold;
    letter-spacing: 1px;
    border-radius: 4px;
  }

  .dealer-name {
    color: #2C3E50;
    font-weight: 600;
    font-size: 14px;
  }

  .reg-num {
    color: #2196F3;
    font-size: 12px;
    font-weight: 500;
  }

  .sales-rep-list-item {
    background-color: #ffffff !important;
    opacity: 1 !important;
  }

  .sales-rep-list-item .sales-rep-name {
    color: #2C3E50 !important;
  }

  .sales-rep-list-item .sales-rep-code {
    color: #1976D2 !important;
  }

  .sales-rep-name {
    color: #2C3E50;
    font-weight: 800;
    font-size: 15px;
  }

  .sales-rep-code {
    color: #1976D2;
    font-weight: 600;
    font-size: 15px;
    margin-top: 2px;
  }

  .table-text {
    color: #555;
    font-size: 13px;
  }

  .address-text {
    color: #666;
    font-size: 12px;
    line-height: 1.4;
  }

  .outstanding-amount {
    .amount {
      color: #2C3E50;
      font-weight: 600;
      font-size: 13px;
    }
  }

  .action-label {
    font-size: 11px;
    font-weight: 700;

    &.active-label {
      color: #800080;
    }

    &.inactive-label {
      color: #BDBDBD;
    }
  }

  .max-width-search {
    max-width: 380px;
  }

  :deep(.v-field__prepend-inner) {
    display: none !important;
  }

  .company-item {
    font-size: 12px;
    margin-bottom: 4px;

    .company-name {
      font-weight: 600;
      color: #2C3E50;
    }

    .company-details {
      color: #757575;
      font-size: 11px;
    }
  }

  .custom-selection-chip {
    max-width: 200px;
    padding: 0 12px !important;
    font-size: 13px !important;
    transition: all 0.2s ease-in-out !important;

    &:hover {
      background-color: #d5d5d5 !important;
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    :deep(.v-chip__close) {
      opacity: 0.6;
      transition: opacity 0.2s;

      &:hover {
        opacity: 1;
      }
    }
  }

  .custom-count-chip {
    min-width: 40px;
    padding: 0 10px !important;
    font-size: 13px !important;
    transition: all 0.2s ease-in-out !important;
    background: linear-gradient(135deg, #800080 0%, #9b2e9b 100%) !important;

    &:hover {
      background: linear-gradient(135deg, #6a006a 0%, #800080 100%) !important;
      transform: translateY(-1px);
      box-shadow: 0 2px 6px rgba(128, 0, 128, 0.3);
      cursor: pointer;
    }

    &:active {
      transform: translateY(0);
    }
  }

  .hover-card-container {
    position: relative;
    display: inline-block;
  }

  .hover-card {
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: 8px;
    min-width: 260px;
    max-width: 360px;
    max-height: 320px;
    overflow-y: auto;
    overflow-x: hidden;
    z-index: 9999;
    background-color: white !important;
    border: 1px solid #ddd !important;
    border-radius: 8px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25) !important;

    .hover-card-list {
      background-color: white;
      padding: 8px 0;
    }

    .hover-card-item {
      padding: 10px 16px;
      color: #333;
      font-size: 13px;
      font-weight: 500;
      border-bottom: 1px solid #f0f0f0;
      background-color: white;

      &:last-child {
        border-bottom: none;
      }

      &:hover {
        background-color: #f5f5f5;
      }
    }
  }

  .hover-card::-webkit-scrollbar {
    width: 6px;
  }

  .hover-card::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  .hover-card::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;

    &:hover {
      background: #a8a8a8;
    }
  }

  .toggle-switch {
    margin-top: -8px;
    margin-bottom: -8px;

    :deep(.v-selection-control) {
      min-height: 32px;
    }

    :deep(.v-switch__track) {
      width: 40px;
      height: 20px;
      border-radius: 10px;
    }

    :deep(.v-switch__thumb) {
      width: 18px;
      height: 18px;
    }
  }

  .view-invoice-button {
    background-color: #800080 !important;
    color: #ffffff !important;
    font-weight: 600;
    font-size: 11px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    border-radius: 4px;
    padding: 6px 12px;
    transition: all 0.2s ease;

    &:hover {
      background-color: #6a006a !important;
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }

    &:active {
      transform: translateY(0);
    }
  }

  .invoice-dialog-card {
    border-radius: 12px !important;
    overflow: hidden;
  }

  .invoice-dialog-title {
    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
    border-bottom: 1px solid #e0e0e0;
  }

  .invoice-dialog-content {
    max-height: 70vh;
    overflow-y: auto;
  }

  .summary-cards {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    align-items: center;
  }

  .summary-chip {
    font-weight: 600;
    padding: 8px 16px;
    
    &:first-child {
      background-color: #4caf50 !important;
      color: white !important;
    }
    
    &:last-child {
      background-color: #1976d2 !important;
      color: white !important;
    }
  }

  .invoice-table {
    :deep(td) {
      padding: 12px 16px !important;
    }
  }

  .invoice-date {
    font-family: monospace;
    font-size: 12px;
    color: #666;
  }

  .text-error {
    color: #c62828 !important;
    font-weight: 600;
  }

  .invoice-document-no {
    color: #1976d2;
    font-family: monospace;
  }

  .document-type-chip {
    font-weight: 600;
    text-transform: uppercase;
    font-size: 11px;
  }

  .amount-positive {
    color: #2e7d32;
    font-weight: 600;
  }

  .amount-negative {
    color: #c62828;
    font-weight: 600;
  }

  .amount-neutral {
    color: #757575;
  }

  .remaining-positive {
    color: #f57c00;
    font-weight: 600;
  }

  .remaining-zero {
    color: #2e7d32;
  }

  .remaining-negative {
    color: #c62828;
  }

  .assigned-positive {
    color: #2e7d32;
  }

  .assigned-negative {
    color: #c62828;
  }

  .assigned-zero {
    color: #757575;
  }
}
</style>